export const API_BASE = '/api' as const;
export const API_V1_BASE = `${API_BASE}/v1` as const;

/** Explicit backend exception mounted outside /api/v1. */
export const API_DRIVER_BASE = `${API_BASE}/driver` as const;
export const API_DRIVER_ORDERS_V1_BASE = `${API_V1_BASE}/driver/orders` as const;

const encodePathSegment = (value: string) => encodeURIComponent(value);

export const API_ROUTES = {
  v1: {
    auth: `${API_V1_BASE}/auth`,
    users: `${API_V1_BASE}/users`,
    cart: `${API_V1_BASE}/cart`,
    checkout: `${API_V1_BASE}/checkout`,
    orders: `${API_V1_BASE}/orders`,
    payments: `${API_V1_BASE}/payments`,
    drivers: `${API_V1_BASE}/drivers`,
    stores: `${API_V1_BASE}/stores`,
    affiliates: `${API_V1_BASE}/affiliates`,
    distance: `${API_V1_BASE}/distance`,
    products: `${API_V1_BASE}/products`,
    productCandidates: `${API_V1_BASE}/productCandidates`,
    masterCatalog: `${API_V1_BASE}/masterCatalog`,
    inventory: `${API_V1_BASE}/inventory`,
    upc: `${API_V1_BASE}/upc`,
    priceIntel: `${API_V1_BASE}/priceIntel`,
    receipts: `${API_V1_BASE}/receipts`,
    scanner: `${API_V1_BASE}/scanner`,
    scanSessions: `${API_V1_BASE}/scanSessions`,
    unmappedProducts: `${API_V1_BASE}/unmappedProducts`,
    bottleReturns: `${API_V1_BASE}/bottleReturns`,
    credits: `${API_V1_BASE}/credits`,
    refunds: `${API_V1_BASE}/refunds`,
    approvals: `${API_V1_BASE}/approvals`,
    audits: `${API_V1_BASE}/audits`,
    settings: `${API_V1_BASE}/settings`,
    health: `${API_V1_BASE}/health`,
    ai: `${API_V1_BASE}/ai`,
    batching: `${API_V1_BASE}/batching`,
    loyaltyPoints: `${API_V1_BASE}/loyaltyPoints`,
    notifications: `${API_V1_BASE}/notifications`,
    tiers: `${API_V1_BASE}/tiers`,
    uploads: `${API_V1_BASE}/uploads`,
    shopper: `${API_V1_BASE}/shopper`,
    ops: `${API_V1_BASE}/ops`,
    admin: `${API_V1_BASE}/admin`,
    errors: `${API_V1_BASE}/errors`,
    observability: `${API_V1_BASE}/observability`,
    fraud: `${API_V1_BASE}/fraud`,
    incidents: `${API_V1_BASE}/incidents`,
    privacy: `${API_V1_BASE}/privacy`,
    driverPayouts: `${API_V1_BASE}/driverPayouts`,
    invites: `${API_V1_BASE}/invites`,
    topupCampaigns: `${API_V1_BASE}/topupCampaigns`,
    addresses: `${API_V1_BASE}/addresses`,
    paymentMethods: `${API_V1_BASE}/paymentMethods`,
    promoCodes: `${API_V1_BASE}/promoCodes`,
    referrals: `${API_V1_BASE}/referrals`,
    deliveryWindows: `${API_V1_BASE}/deliveryWindows`,
    support: `${API_V1_BASE}/support`,
    reviews: `${API_V1_BASE}/reviews`,
    stockNotifications: `${API_V1_BASE}/stockNotifications`,
    mcp: `${API_V1_BASE}/mcp`,
    push: `${API_V1_BASE}/push`,
    home: `${API_V1_BASE}/home`,
    crowdsource: `${API_V1_BASE}/crowdsource`,
    subscriptions: `${API_V1_BASE}/subscriptions`,
    chat: `${API_V1_BASE}/chat`,
    ucp: `${API_V1_BASE}/ucp`,
    acp: `${API_V1_BASE}/acp`,
    agenticCommerce: `${API_V1_BASE}/agentic-commerce`,
    a2a: `${API_V1_BASE}/a2a`,
    gifts: `${API_V1_BASE}/gifts`,
    impact: `${API_V1_BASE}/impact`,
    weather: `${API_V1_BASE}/weather`
  },
  driver: API_DRIVER_BASE
} as const;

type AdminImpersonationStartPath = `${typeof API_ROUTES.v1.admin}/impersonation/start`;
type AdminImpersonationStopPath = `${typeof API_ROUTES.v1.admin}/impersonation/stop`;
type AdminImpersonationStatusPath = `${typeof API_ROUTES.v1.admin}/impersonation/status`;
type DriverDirectoryPath = typeof API_ROUTES.v1.drivers;
type DriverOrderActionPath<Action extends string> =
  `${typeof API_ROUTES.v1.drivers}/${string}/orders/${string}/${Action}`;
type DriverCompleteDeliveryPath = `${typeof API_ROUTES.v1.drivers}/${string}/orders/completeDelivery`;
type DriverCashOrderPaymentPath = `${typeof API_DRIVER_ORDERS_V1_BASE}/${string}/cash`;
type DriverPayoutLedgerPath = `${typeof API_ROUTES.v1.driverPayouts}/${string}/ledger`;
type DriverLocationPath = `${typeof API_ROUTES.v1.drivers}/${string}/location`;
type DriverOnboardingDocsPath = `${typeof API_ROUTES.v1.drivers}/${string}/onboardingDocs`;
type DriverReceiptCapturePath = `${typeof API_DRIVER_BASE}/receiptCapture/${string}`;
type DriverReceiptCaptureItemsPath = `${typeof API_DRIVER_BASE}/receiptCapture/${string}/items`;
type DriverOrderShoppingListPath = `${typeof API_DRIVER_BASE}/order/${string}/shoppingList`;
type DriverOrderItemsNotFoundPath = `${typeof API_DRIVER_BASE}/order/${string}/itemsNotFound`;
type DriverOrderItemNotFoundBySkuPath = `${typeof API_DRIVER_BASE}/order/${string}/itemsNotFound/${string}`;

const ADMIN_IMPERSONATION_START: AdminImpersonationStartPath = `${API_ROUTES.v1.admin}/impersonation/start`;
const ADMIN_IMPERSONATION_STOP: AdminImpersonationStopPath = `${API_ROUTES.v1.admin}/impersonation/stop`;
const ADMIN_IMPERSONATION_STATUS: AdminImpersonationStatusPath = `${API_ROUTES.v1.admin}/impersonation/status`;
const DRIVER_DIRECTORY: DriverDirectoryPath = API_ROUTES.v1.drivers;

export const API_PATHS = {
  auth: {
    logout: `${API_ROUTES.v1.auth}/logout`,
    register: `${API_ROUTES.v1.auth}/register`,
    login: `${API_ROUTES.v1.auth}/login`,
    session: `${API_ROUTES.v1.auth}/session`,
    refresh: `${API_ROUTES.v1.auth}/refresh`,
    tokenRefresh: `${API_ROUTES.v1.auth}/token/refresh`,
    forgotPassword: `${API_ROUTES.v1.auth}/forgotPassword`,
    resetPassword: `${API_ROUTES.v1.auth}/resetPassword`,
    verifyEmail: `${API_ROUTES.v1.auth}/verifyEmail`,
    resendVerification: `${API_ROUTES.v1.auth}/resendVerification`,
    sessionsIntrospect: `${API_ROUTES.v1.auth}/sessions/introspect`,
    sessionsRevokeAll: `${API_ROUTES.v1.auth}/sessions/revokeAll`,
    sessionById: (sessionId: string) => `${API_ROUTES.v1.auth}/sessions/${encodePathSegment(sessionId)}`,
    changePassword: `${API_ROUTES.v1.auth}/changePassword`,
    stepUpConfirm: `${API_ROUTES.v1.auth}/stepUp/confirm`,
    phoneVerify: `${API_ROUTES.v1.auth}/phone/verify`,
    firebaseGoogle: `${API_ROUTES.v1.auth}/firebase/google`,
    mfaStatus: `${API_ROUTES.v1.auth}/mfa/status`,
    mfaBegin: `${API_ROUTES.v1.auth}/mfa/begin`,
    mfaConfirm: `${API_ROUTES.v1.auth}/mfa/confirm`,
    mfaDisable: `${API_ROUTES.v1.auth}/mfa/disable`,
    mfaRegenerateBackupCodes: `${API_ROUTES.v1.auth}/mfa/backupCodes/regenerate`,
    apiKeys: `${API_ROUTES.v1.auth}/apiKeys`,
    apiKeyById: (keyId: string) => `${API_ROUTES.v1.auth}/apiKeys/${encodePathSegment(keyId)}`
  },
  users: {
    me: `${API_ROUTES.v1.users}/me`,
    meProfile: `${API_ROUTES.v1.users}/me/profile`,
    meSendReset: `${API_ROUTES.v1.users}/me/sendReset`,
    meDataExport: `${API_ROUTES.v1.users}/me/dataExport`,
    agentKey: `${API_ROUTES.v1.users}/me/agentKey`,
    notificationPreferencesMe: `${API_ROUTES.v1.users}/me/notificationPreferences`,
    ledger: (userId: string) => `${API_ROUTES.v1.users}/${encodePathSegment(userId)}/ledger`,
    cart: (userId: string) => `${API_ROUTES.v1.users}/${encodePathSegment(userId)}/cart`,
    sendReset: (userId: string) => `${API_ROUTES.v1.users}/${encodePathSegment(userId)}/sendReset`,
    bottleReturns: (userId: string) => `${API_ROUTES.v1.users}/${encodePathSegment(userId)}/bottleReturns`,
    tierPolicy: `${API_ROUTES.v1.users}/tierPolicy`,
    myTeens: `${API_ROUTES.v1.users}/teen/myTeens`,
    approveTeen: `${API_ROUTES.v1.users}/teen/approveLink`,
    updateTeenControls: (teenId: string) => `${API_ROUTES.v1.users}/teen/${encodePathSegment(teenId)}/controls`
  },
  cart: {
    root: API_ROUTES.v1.cart,
    update: `${API_ROUTES.v1.cart}/update`,
    returnUpcs: `${API_ROUTES.v1.cart}/returnUpcs`,
    checkoutDraft: `${API_ROUTES.v1.cart}/checkoutDraft`
  },
  payments: {
    captureByOrder: (orderId: string) => `${API_ROUTES.v1.payments}/${encodePathSegment(orderId)}/capture`,
    refund: `${API_ROUTES.v1.payments}/refund`,
    checkoutSession: (orderId: string) => `${API_ROUTES.v1.payments}/${encodePathSegment(orderId)}/checkoutSession`,
    checkoutSessionStatus: `${API_ROUTES.v1.payments}/checkoutSession/status`,
    byOrder: (orderId: string) => `${API_ROUTES.v1.payments}/order/${encodePathSegment(orderId)}`,
    connectSession: `${API_ROUTES.v1.payments}/connect/session`
  },
  checkout: {
    preview: `${API_ROUTES.v1.checkout}/preview`,
    quote: `${API_ROUTES.v1.checkout}/quote`,
    findStores: `${API_ROUTES.v1.checkout}/findStores`,
    batches: `${API_ROUTES.v1.checkout}/batches`,
    batchStatus: (batchId: string) => `${API_ROUTES.v1.checkout}/batches/${encodePathSegment(batchId)}`,
    updateInventory: `${API_ROUTES.v1.checkout}/inventory/update`,
    productInventory: (productId: string) => `${API_ROUTES.v1.checkout}/inventory/${encodePathSegment(productId)}`,
    requestDriver: `${API_ROUTES.v1.checkout}/requestDriver`
  },
  orders: {
    root: API_ROUTES.v1.orders,
    search: `${API_ROUTES.v1.orders}/search`,
    byId: (orderId: string) => `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}`,
    tracking: (orderId: string) => `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}/tracking`,
    events: (orderId: string) => `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}/events`,
    timelineGapFill: (orderId: string) =>
      `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}/timeline/gap-fill`,
    deliveryContact: (orderId: string) =>
      `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}/deliveryContact`,
    chat: (orderId: string) =>
      `${API_ROUTES.v1.chat}/${encodePathSegment(orderId)}`,
    receipt: (orderId: string, format: 'text' | 'html' = 'html') =>
      `${API_ROUTES.v1.orders}/${encodePathSegment(orderId)}/receipt?format=${encodeURIComponent(format)}`
  },
  stores: {
    root: API_ROUTES.v1.stores,
    enrich: `${API_ROUTES.v1.stores}/enrich`,
    byId: (storeId: string) => `${API_ROUTES.v1.stores}/${encodePathSegment(storeId)}`,
    approve: (storeId: string) => `${API_ROUTES.v1.stores}/${encodePathSegment(storeId)}/approve`,
    shoppingAvailability: `${API_ROUTES.v1.stores}/shoppingAvailability`,
    inventory: (storeId: string, query = '') =>
      `${API_ROUTES.v1.inventory}/storeInventory/${storeId}${query ? `?${query}` : ''}`,
    productLocation: (storeId: string, productId: string) =>
      `${API_ROUTES.v1.stores}/${encodePathSegment(storeId)}/products/${encodePathSegment(productId)}/location`
  },
  affiliates: {
    applications: `${API_ROUTES.v1.affiliates}/applications`,
    rejectStore: (storeId: string) =>
      `${API_ROUTES.v1.affiliates}/stores/${encodePathSegment(storeId)}/reject`,
    shelf: (storeId: string) =>
      `${API_ROUTES.v1.affiliates}/stores/${encodePathSegment(storeId)}/shelf`
  },
  inventory: {
    intelligence: `${API_ROUTES.v1.inventory}/intelligence`,
    pricingRecommendations: `${API_ROUTES.v1.inventory}/pricingRecommendations`,
    pricingRecommendationApprove: (recommendationId: string) =>
      `${API_ROUTES.v1.inventory}/pricingRecommendations/${encodePathSegment(recommendationId)}/approve`,
    pricingRecommendationReject: (recommendationId: string) =>
      `${API_ROUTES.v1.inventory}/pricingRecommendations/${encodePathSegment(recommendationId)}/reject`,
    storeInventory: (storeId: string) => `${API_ROUTES.v1.inventory}/storeInventory/${encodePathSegment(storeId)}`,
    upsertStoreInventory: `${API_ROUTES.v1.inventory}/storeInventory`
  },
  ops: {
    trendRecommendations: `${API_ROUTES.v1.ops}/trends/recommendations`,
    manualTrendCandidate: `${API_ROUTES.v1.ops}/trends/candidates`,
    trendDecision: (candidateId: string) =>
      `${API_ROUTES.v1.ops}/trends/candidates/${encodePathSegment(candidateId)}/decision`,
    marketingRecommendations: `${API_ROUTES.v1.ops}/marketing/recommendations`,
    marketingDecision: (recommendationId: string) =>
      `${API_ROUTES.v1.ops}/marketing/recommendations/${encodePathSegment(recommendationId)}/decision`,
    autopilotStatus: `${API_ROUTES.v1.ops}/autopilot/status`,
    autopilotUsage: `${API_ROUTES.v1.ops}/autopilot/usage`
  },
  shopper: {
    assignments: `${API_ROUTES.v1.shopper}/assignments`,
    assignmentAccept: (assignmentId: string) =>
      `${API_ROUTES.v1.shopper}/assignments/${encodePathSegment(assignmentId)}/accept`,
    assignmentReject: (assignmentId: string) =>
      `${API_ROUTES.v1.shopper}/assignments/${encodePathSegment(assignmentId)}/reject`,
    orderTasks: (orderId: string) => `${API_ROUTES.v1.shopper}/orders/${encodePathSegment(orderId)}/tasks`,
    inviteTrusted: `${API_ROUTES.v1.shopper}/profile/inviteTrusted`,
    trustedShoppers: `${API_ROUTES.v1.shopper}/profile/trustedShoppers`,
    trustedActivate: (userId: string) =>
      `${API_ROUTES.v1.shopper}/profile/trusted/${encodePathSegment(userId)}/activate`,
    trustedDeactivationRequest: (userId: string) =>
      `${API_ROUTES.v1.shopper}/profile/trusted/${encodePathSegment(userId)}/deactivation/request`,
    trustedDeactivationApprove: (userId: string) =>
      `${API_ROUTES.v1.shopper}/profile/trusted/${encodePathSegment(userId)}/deactivation/approve`
  },
  products: {
    live: `${API_ROUTES.v1.products}/live`,
    byIdentifier: (identifier: string) => `${API_ROUTES.v1.products}/${encodePathSegment(identifier)}`,
    temperatureQuote: (productId: string) =>
      `${API_ROUTES.v1.products}/${encodePathSegment(productId)}/temperatureQuote`
  },
  masterCatalog: {
    root: API_ROUTES.v1.masterCatalog,
    byId: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}`,
    upcs: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/upcs`,
    mappings: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/mappings`,
    images: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/images`,
    nutrition: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/nutrition`,
    aliases: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/aliases`,
    tags: (productId: string) =>
      `${API_ROUTES.v1.masterCatalog}/${encodePathSegment(productId)}/tags`
  },
  upc: {
    root: API_ROUTES.v1.upc,
    scan: `${API_ROUTES.v1.upc}/scan`,
    eligibility: (upc: string) => `${API_ROUTES.v1.upc}/eligibility?upc=${encodeURIComponent(upc)}`,
    off: (upc: string) => `${API_ROUTES.v1.upc}/off/${encodePathSegment(upc)}`,
    link: `${API_ROUTES.v1.upc}/link`,
    quickLink: `${API_ROUTES.v1.upc}/quick-link`,
    items: `${API_ROUTES.v1.upc}/items`,
    quarantine: `${API_ROUTES.v1.upc}/quarantine`,
    quarantineDecision: (upc: string) => `${API_ROUTES.v1.upc}/quarantine/${encodeURIComponent(upc)}/decision`,
    candidates: `${API_ROUTES.v1.upc}/candidates`,
    candidateVerify: (upc: string) =>
      `${API_ROUTES.v1.upc}/candidates/${encodeURIComponent(upc)}/verify`,
    /** DELETE /api/v1/upc/:upc — canonical registry row (not under /items). */
    itemById: (upc: string) => `${API_ROUTES.v1.upc}/${encodeURIComponent(upc)}`
  },
  unmappedProducts: {
    root: API_ROUTES.v1.unmappedProducts,
    history: (id: string) => `${API_ROUTES.v1.unmappedProducts}/${encodePathSegment(id)}/history`,
    map: (id: string) => `${API_ROUTES.v1.unmappedProducts}/${encodePathSegment(id)}/map`,
    ignore: (id: string) => `${API_ROUTES.v1.unmappedProducts}/${encodePathSegment(id)}/ignore`,
    promote: (id: string) => `${API_ROUTES.v1.unmappedProducts}/${encodePathSegment(id)}/promote`,
    registryPreview: (id: string) => `${API_ROUTES.v1.unmappedProducts}/${encodePathSegment(id)}/registryPreview`
  },
  priceIntel: {
    observations: `${API_ROUTES.v1.priceIntel}/observations`,
    manualMatch: `${API_ROUTES.v1.priceIntel}/aliases/manualMatch`
  },
  scanner: {
    lookup: (barcode: string) => `${API_ROUTES.v1.scanner}/lookup/${encodeURIComponent(barcode)}`,
    lookupWithStore: (barcode: string, storeId: string) =>
      `${API_ROUTES.v1.scanner}/lookup?barcode=${encodeURIComponent(barcode)}&storeId=${encodeURIComponent(storeId)}`
  },
  ai: {
    models: `${API_ROUTES.v1.ai}/models`,
    chat: `${API_ROUTES.v1.ai}/chat`,
    search: `${API_ROUTES.v1.ai}/search`,
    explainCheckout: `${API_ROUTES.v1.ai}/explainCheckout`,
    productScan: `${API_ROUTES.v1.ai}/productScan`,
    inventoryAudit: `${API_ROUTES.v1.ai}/inventoryAudit`,
    opsSummary: `${API_ROUTES.v1.ai}/opsSummary`,
    auditSummary: `${API_ROUTES.v1.ai}/auditSummary`,
    issueExplain: `${API_ROUTES.v1.ai}/issueExplain`,
    validateAddress: `${API_ROUTES.v1.ai}/validateAddress`,
    recommendations: `${API_ROUTES.v1.ai}/recommendations`,
    categorizeProduct: `${API_ROUTES.v1.ai}/categorizeProduct`,
    demandForecast: `${API_ROUTES.v1.ai}/demandForecast`,
    naturalSearch: `${API_ROUTES.v1.ai}/naturalSearch`,
    protocolsStatus: `${API_ROUTES.v1.ai}/protocols/status`
  },
  support: {
    faq: `${API_ROUTES.v1.support}/faq`,
    tickets: API_ROUTES.v1.support
  },
  approvals: {
    list: `${API_ROUTES.v1.approvals}`,
    pending: `${API_ROUTES.v1.approvals}/pending`,
    request: `${API_ROUTES.v1.approvals}/request`,
    review: (id: string) => `${API_ROUTES.v1.approvals}/${encodePathSegment(id)}/review`
  },
  home: {
    capabilities: API_ROUTES.v1.home
  },
  crowdsource: {
    submit: `${API_ROUTES.v1.crowdsource}/submit`,
    audits: `${API_ROUTES.v1.crowdsource}/audits`,
    review: (submissionId: string) => `${API_ROUTES.v1.crowdsource}/audits/${encodePathSegment(submissionId)}/review`
  },
  receipts: {
    list: API_ROUTES.v1.receipts,
    byId: (jobId: string) => `${API_ROUTES.v1.receipts}/${encodePathSegment(jobId)}`,
    approve: (jobId: string) => `${API_ROUTES.v1.receipts}/${encodePathSegment(jobId)}/approve`,
    reject: (jobId: string) => `${API_ROUTES.v1.receipts}/${encodePathSegment(jobId)}/reject`,
    delete: (captureId: string) => `${API_ROUTES.v1.receipts}/${encodePathSegment(captureId)}`,
    learningEvents: `${API_ROUTES.v1.receipts}/learningEvents`
  },
  uploads: {
    sign: `${API_ROUTES.v1.uploads}/sign`,
    finalize: `${API_ROUTES.v1.uploads}/finalize`
  },
  bottleReturns: {
    root: API_ROUTES.v1.bottleReturns,
    myReturns: `${API_ROUTES.v1.bottleReturns}/myReturns`,
    appeal: (id: string) => `${API_ROUTES.v1.bottleReturns}/${encodePathSegment(id)}/appeal`,
    review: (id: string) => `${API_ROUTES.v1.bottleReturns}/${encodePathSegment(id)}/review`,
    auditSettle: (id: string) => `${API_ROUTES.v1.bottleReturns}/${encodePathSegment(id)}/audit-settle`,
    statutoryLedger: `${API_ROUTES.v1.bottleReturns}/statutory-ledger`,
    statutoryLedgerCsv: `${API_ROUTES.v1.bottleReturns}/statutory-ledger/export-csv`
  },
  refunds: {
    root: API_ROUTES.v1.refunds,
    byId: (id: string) => `${API_ROUTES.v1.refunds}/${encodePathSegment(id)}`,
    review: (id: string) => `${API_ROUTES.v1.refunds}/${encodePathSegment(id)}/review`
  },
  invites: {
    root: API_ROUTES.v1.invites,
    byId: (id: string) => `${API_ROUTES.v1.invites}/${encodePathSegment(id)}`,
    print: (id: string) => `${API_ROUTES.v1.invites}/${encodePathSegment(id)}/print`,
    redeem: `${API_ROUTES.v1.invites}/redeem`
  },
  referrals: {
    me: `${API_ROUTES.v1.referrals}/me`,
    claim: `${API_ROUTES.v1.referrals}/claim`
  },
  topupCampaigns: {
    root: API_ROUTES.v1.topupCampaigns,
    active: `${API_ROUTES.v1.topupCampaigns}/active`,
    byId: (id: string) => `${API_ROUTES.v1.topupCampaigns}/${encodePathSegment(id)}`,
    activate: (id: string) => `${API_ROUTES.v1.topupCampaigns}/${encodePathSegment(id)}/activate`,
    end: (id: string) => `${API_ROUTES.v1.topupCampaigns}/${encodePathSegment(id)}/end`,
    cancel: (id: string) => `${API_ROUTES.v1.topupCampaigns}/${encodePathSegment(id)}/cancel`
  },
  credits: {
    balance: `${API_ROUTES.v1.credits}/balance`,
    ledger: `${API_ROUTES.v1.credits}/ledger`,
    adjust: `${API_ROUTES.v1.credits}/adjust`,
    topup: `${API_ROUTES.v1.credits}/topup`,
    topupPreview: `${API_ROUTES.v1.credits}/topup/preview`,
    cashout: `${API_ROUTES.v1.credits}/cashout`
  },
  driverPayouts: {
    root: API_ROUTES.v1.driverPayouts,
    scheduleAndBalance: `${API_ROUTES.v1.driverPayouts}/schedule-and-balance`,
    treasuryOverview: `${API_ROUTES.v1.driverPayouts}/treasury-overview`,
    runBatch: `${API_ROUTES.v1.driverPayouts}/run-batch`,
    ledger: (driverId: string) => `${API_ROUTES.v1.driverPayouts}/${encodePathSegment(driverId)}/ledger`,
    taxPacket: (driverId: string) => `${API_ROUTES.v1.driverPayouts}/${encodePathSegment(driverId)}/tax-packet`
  },
  privacy: {
    myRequests: `${API_ROUTES.v1.privacy}/requests/mine`,
    staffRequests: `${API_ROUTES.v1.privacy}/requests`,
    exportDownload: (requestId: string) => `${API_ROUTES.v1.privacy}/exports/${encodePathSegment(requestId)}/download`,
    approveExport: (requestId: string) => `${API_ROUTES.v1.privacy}/requests/${encodePathSegment(requestId)}/approveExport`,
    approveDeletion: (requestId: string) => `${API_ROUTES.v1.privacy}/requests/${encodePathSegment(requestId)}/approveDeletion`,
    reject: (requestId: string) => `${API_ROUTES.v1.privacy}/requests/${encodePathSegment(requestId)}/reject`
  },
  loyaltyPoints: {
    me: `${API_ROUTES.v1.loyaltyPoints}/me`,
    award: `${API_ROUTES.v1.loyaltyPoints}/award`,
    redeem: `${API_ROUTES.v1.loyaltyPoints}/redeem`
  },
  errors: {
    root: API_ROUTES.v1.errors
  },
  observability: {
    rumVital: `${API_ROUTES.v1.observability}/rumVital`
  },
  notifications: {
    telemetry: `${API_ROUTES.v1.notifications}/telemetry`
  },
  admin: {
    impersonationStart: ADMIN_IMPERSONATION_START,
    impersonationStop: ADMIN_IMPERSONATION_STOP,
    impersonationStatus: ADMIN_IMPERSONATION_STATUS,
    analyticsOverview: `${API_ROUTES.v1.admin}/analytics/overview`,
    unlockUser: (userId: string) => `${API_ROUTES.v1.admin}/users/${encodePathSegment(userId)}/unlock`
  },
  push: {
    subscribe: `${API_ROUTES.v1.push}/subscribe`,
    unsubscribe: `${API_ROUTES.v1.push}/unsubscribe`,
    /** Native Android/iOS FCM registration (not VAPID browser push). */
    deviceToken: `${API_ROUTES.v1.push}/deviceToken`,
    unregisterDeviceToken: `${API_ROUTES.v1.push}/deviceToken/unregister`
  },
  batching: {
    root: API_ROUTES.v1.batching,
    byId: (batchId: string) => `${API_ROUTES.v1.batching}/${encodePathSegment(batchId)}`,
    assign: (batchId: string) => `${API_ROUTES.v1.batching}/${encodePathSegment(batchId)}/assign`,
    status: (batchId: string) => `${API_ROUTES.v1.batching}/${encodePathSegment(batchId)}/status`,
    autoCluster: `${API_ROUTES.v1.batching}/auto-cluster`,
    shoppingChecklist: (batchId: string) => `${API_ROUTES.v1.batching}/${encodePathSegment(batchId)}/shopping-checklist`
  },
  weather: {
    root: API_ROUTES.v1.weather
  },
  driver: {
    rootV1: DRIVER_DIRECTORY,
    directoryV1: DRIVER_DIRECTORY,
    connectSession: `${API_ROUTES.v1.drivers}/accountSession`,
    acceptOrder: `${API_DRIVER_BASE}/acceptOrder`,
    declineOrder: `${API_DRIVER_BASE}/declineOrder`,
    pickupOrder: `${API_DRIVER_BASE}/pickupOrder`,
    startDelivery: `${API_DRIVER_BASE}/startDelivery`,
    completeDelivery: `${API_DRIVER_BASE}/completeDelivery`,
    /** v1 lifecycle endpoints (HMAC-protected for non-session callers). */
    acceptOrderV1: (driverId: string, orderId: string): DriverOrderActionPath<'accept'> =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/${encodePathSegment(orderId)}/accept`,
    pickupOrderV1: (driverId: string, orderId: string): DriverOrderActionPath<'pickup'> =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/${encodePathSegment(orderId)}/pickup`,
    assignOrderV1: (driverId: string, orderId: string): DriverOrderActionPath<'assign'> =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/${encodePathSegment(orderId)}/assign`,
    completeDeliveryV1: (driverId: string): DriverCompleteDeliveryPath =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/completeDelivery`,
    cashPayoutAckV1: (driverId: string, orderId: string): DriverOrderActionPath<'cashPayout'> =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/${encodePathSegment(orderId)}/cashPayout`,
    cashOrderPaymentV1: (orderId: string): DriverCashOrderPaymentPath =>
      `${API_DRIVER_ORDERS_V1_BASE}/${encodePathSegment(orderId)}/cash`,
    payoutLedgerV1: (driverId: string): DriverPayoutLedgerPath =>
      `${API_ROUTES.v1.driverPayouts}/${encodePathSegment(driverId)}/ledger`,
    updateLocationV1: (driverId: string): DriverLocationPath => `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/location`,
    onboardingDocs: (driverId: string): DriverOnboardingDocsPath =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/onboardingDocs`,
    onboardingDocsV1: (driverId: string): DriverOnboardingDocsPath =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/onboardingDocs`,
    shoppingList: (orderId: string): DriverOrderShoppingListPath => `${API_DRIVER_BASE}/order/${encodePathSegment(orderId)}/shoppingList`,
    earnings: `${API_DRIVER_BASE}/earnings`,
    performance: `${API_DRIVER_BASE}/performance`,
    assignedOrders: `${API_DRIVER_BASE}/assignedOrders`,
    pendingOrders: `${API_DRIVER_BASE}/pendingOrders`,
    receiptAliases: (storeId: string) => `${API_DRIVER_BASE}/receiptAliases?storeId=${encodeURIComponent(storeId)}`,
    receiptNoiseRules: (storeId: string) => `${API_DRIVER_BASE}/receiptNoiseRules?storeId=${encodeURIComponent(storeId)}`,
    uploadReceiptImage: `${API_DRIVER_BASE}/uploadReceiptImage`,
    parseReceiptFrame: `${API_DRIVER_BASE}/receiptParseFrame`,
    receiptStoreCandidates: `${API_DRIVER_BASE}/receiptStoreCandidates`,
    createCapture: `${API_DRIVER_BASE}/receiptCapture`,
    triggerParse: `${API_DRIVER_BASE}/receiptParse`,
    getCapture: (captureId: string): DriverReceiptCapturePath => `${API_DRIVER_BASE}/receiptCapture/${encodePathSegment(captureId)}`,
    getCaptureItems: (captureId: string): DriverReceiptCaptureItemsPath =>
      `${API_DRIVER_BASE}/receiptCapture/${encodePathSegment(captureId)}/items`,
    receiptHealth: `${API_DRIVER_BASE}/receiptHealth`,
    resetReview: `${API_DRIVER_BASE}/receiptResetReview`,
    lockCapture: `${API_DRIVER_BASE}/receiptLock`,
    unlockCapture: `${API_DRIVER_BASE}/receiptUnlock`,
    confirmMatch: `${API_DRIVER_BASE}/receiptConfirmMatch`,
    createNoiseRule: `${API_DRIVER_BASE}/receiptNoiseRule`,
    captureSummary: `${API_DRIVER_BASE}/captureSummary`,
    priceUpdateManual: `${API_DRIVER_BASE}/receiptPriceUpdateManual`,
    itemsNotFound: (orderId: string): DriverOrderItemsNotFoundPath =>
      `${API_DRIVER_BASE}/order/${encodePathSegment(orderId)}/itemsNotFound`,
    itemNotFoundBySku: (orderId: string, sku: string): DriverOrderItemNotFoundBySkuPath =>
      `${API_DRIVER_BASE}/order/${encodePathSegment(orderId)}/itemsNotFound/${encodePathSegment(sku)}`,
    verifyReturnsV1: (driverId: string, orderId: string): DriverOrderActionPath<'verifyReturns'> =>
      `${API_ROUTES.v1.drivers}/${encodePathSegment(driverId)}/orders/${encodePathSegment(orderId)}/verifyReturns`
  }
} as const;
